import React from 'react';
import SideNav from '../../Common/SideNav/SideNav';
import { hasActiveChild, isRouteActive, isNavItem, navBubble } from '../index';
import { NavSection, NavNestedSection } from '../types';

interface SideNavNestedSectionProps {
  section: NavSection;
  sectionIndex: number;
  pathname: string;
  isOpen: boolean;
  openSections: Set<string>;
  toggleSection: (key: string) => void;
  trackAction: (data: { elementId: string; type: 'click' }) => void;
}

/**
 * Renders a section with nested sub-sections (nesting levels 0 -> 1 -> 2)
 */
export const SideNavNestedSection = ({
  section,
  sectionIndex,
  pathname,
  isOpen,
  openSections,
  toggleSection,
  trackAction,
}: SideNavNestedSectionProps) => {
  return (
    <>
      <SideNav.GroupToggle
        title={section.title!}
        url={section.route?.[0]}
        isActive={isRouteActive(pathname, section.route)}
        hasActiveChild={hasActiveChild(pathname, section)}
        withReactRouter={!!(section.route && !section.route[0]?.startsWith('http'))}
        showMenuItems={isOpen}
        onClick={() => toggleSection(String(sectionIndex))}
      />
      <SideNav.Group>
        {isOpen &&
          section.children.map((child, childIndex) => {
            if (isNavItem(child)) {
              // Nav item (level 1)
              return (
                <SideNav.Item
                  key={`${sectionIndex}-${childIndex}`}
                  title={child.label}
                  url={child.route[0]}
                  isActive={isRouteActive(pathname, child.route)}
                  isVisible={isOpen}
                  openInNewTab={child.target === '_blank'}
                  withReactRouter={!child.target}
                  bubble={navBubble(child.bubble)}
                  nestingLevel={1}
                  onClick={() => trackAction({ elementId: `sidebar-link-${child.id}`, type: 'click' })}
                />
              );
            }
            // Nested section with toggle
            const nestedSection = child as NavNestedSection;
            const nestedIsOpen = openSections.has(`${sectionIndex}-${childIndex}`);
            return (
              <React.Fragment key={`${sectionIndex}-${childIndex}`}>
                <SideNav.GroupToggle
                  title={nestedSection.title}
                  url={nestedSection.route?.[0]}
                  isActive={isRouteActive(pathname, nestedSection.route)}
                  hasActiveChild={hasActiveChild(pathname, nestedSection)}
                  withReactRouter={!!(nestedSection.route && !nestedSection.route[0]?.startsWith('http'))}
                  showMenuItems={nestedIsOpen}
                  onClick={() => toggleSection(`${sectionIndex}-${childIndex}`)}
                  nestingLevel={1}
                />
                <SideNav.Group>
                  {nestedSection.items.map((item, itemIndex) => {
                    return (
                      <SideNav.Item
                        key={`${sectionIndex}-${childIndex}-${itemIndex}`}
                        title={item.label}
                        url={item.route[0]}
                        isActive={isRouteActive(pathname, item.route)}
                        isVisible={nestedIsOpen}
                        openInNewTab={item.target === '_blank'}
                        withReactRouter={!item.target}
                        bubble={navBubble(item.bubble)}
                        nestingLevel={2}
                        onClick={() => trackAction({ elementId: `sidebar-link-${item.id}`, type: 'click' })}
                      />
                    );
                  })}
                </SideNav.Group>
              </React.Fragment>
            );
          })}
      </SideNav.Group>
    </>
  );
};
